import type { RouteLocationRaw } from "vue-router"
import type { PregnancyStartType } from "@/types/pregnancy"
import { useRouter } from "vue-router"

/** A resolved application route, as returned by every `route*()` helper below. */
export interface EntityRoute {
	navigate: () => void
	replace: () => void
	valueOf: () => string
	toString: () => string
	/** Relative href — use for `<a>` targets so middle-click / open-in-new-tab work. */
	href: () => string
	/** Absolute URL — use only for copy-link / share UI. */
	fullUrl: () => string
}

/** Builds the slugged `patientId` route param (`{name}-{id}`) when a name is provided. */
function slugPatientId(params: { patientId: string | number; patientName?: string }): string | number {
	return params.patientName ? `${params.patientName}-${params.patientId}` : params.patientId
}

export function useNavigation() {
	const router = useRouter()

	function routerConstructor(route: RouteLocationRaw): EntityRoute {
		return {
			navigate: () => router.push(route),
			replace: () => router.replace(route),
			valueOf: () => router.resolve(route).href,
			toString: () => router.resolve(route).href,
			href: () => router.resolve(route).href,
			fullUrl: () => {
				const resolved = router.resolve(route)
				return `${window.location.protocol}//${window.location.host}${resolved.href}`
			}
		}
	}

	function routeDashboard(): EntityRoute {
		return routerConstructor({ name: "Dashboard" })
	}

	function routePatients(params?: {
		patientId?: string | number | "create"
		patientName?: string
		activeTab?:
			"exams" | "therapies" | "gynecological_record" | "obstetric_record" | "visits_history" | "personal_data"
		options?: {
			startDateType?: PregnancyStartType
			startDate?: number
			redatingDays?: number
		}
	}): EntityRoute {
		if (!params?.patientId) {
			return routerConstructor({ name: "PatientsList" })
		}

		if (params?.patientId === "create") {
			return routerConstructor({
				name: "PatientCreate",
				query: {
					start_date_type: params?.options?.startDateType,
					start_date: params?.options?.startDate,
					redating_days: params?.options?.redatingDays
				}
			})
		}

		const patientId = slugPatientId({
			patientId: params.patientId,
			patientName: params.patientName
		})
		return routerConstructor({
			name: "PatientOverview",
			params: { patientId },
			query: {
				patient_archive_active_tab: params?.activeTab ? JSON.stringify(params.activeTab) : undefined
			}
		})
	}

	function routePregnancyCalculator(params?: { patientId?: number }): EntityRoute {
		return routerConstructor({ name: "Tools.PregnancyCalculator", query: { patient_id: params?.patientId } })
	}

	function routeMonitoring(params?: { patientId?: number }): EntityRoute {
		return routerConstructor({ name: "Tools.Monitoring", query: { patient_id: params?.patientId } })
	}

	function routeBiometry(params?: { patientId?: number }): EntityRoute {
		return routerConstructor({ name: "Tools.Biometry", query: { patient_id: params?.patientId } })
	}

	function routeProfile(params?: { activeTab?: string }): EntityRoute {
		return routerConstructor({
			name: "Profile",
			query: {
				profile_active_tab: params?.activeTab ? JSON.stringify({ activeTab: params.activeTab }) : undefined
			}
		})
	}

	function routeVisit(params: {
		patientId: number | string
		patientName?: string
		visitId: number | "create"
	}): EntityRoute {
		const patientId = slugPatientId(params)

		if (params.visitId === "create") {
			return routerConstructor({ name: "VisitCreate", params: { patientId } })
		}

		return routerConstructor({ name: "VisitOverview", params: { patientId, visitId: params.visitId } })
	}

	function routeExam(params: {
		patientId: number | string
		patientName?: string
		examId: number | "create"
	}): EntityRoute {
		const patientId = slugPatientId(params)

		if (params.examId === "create") {
			return routerConstructor({ name: "ExamCreate", params: { patientId } })
		}

		return routerConstructor({ name: "ExamOverview", params: { patientId, examId: params.examId } })
	}

	function routeTherapy(params: {
		patientId: number | string
		patientName?: string
		therapyId: number | "create"
	}): EntityRoute {
		const patientId = slugPatientId(params)

		if (params.therapyId === "create") {
			return routerConstructor({ name: "TherapyCreate", params: { patientId } })
		}

		return routerConstructor({ name: "TherapyOverview", params: { patientId, therapyId: params.therapyId } })
	}

	function routePregnancy(params: {
		patientId: number | string
		patientName?: string
		pregnancyId: number | "create"
	}): EntityRoute {
		const patientId = slugPatientId(params)

		if (params.pregnancyId === "create") {
			return routerConstructor({ name: "PregnancyCreate", params: { patientId } })
		}

		return routerConstructor({ name: "PregnancyOverview", params: { patientId, pregnancyId: params.pregnancyId } })
	}

	function routeRegister(): EntityRoute {
		return routerConstructor({ name: "Register" })
	}

	function routeLogin(): EntityRoute {
		return routerConstructor({ name: "Login" })
	}

	return {
		routeDashboard,
		routePatients,
		routePregnancyCalculator,
		routeMonitoring,
		routeBiometry,
		routePregnancy,
		routeProfile,
		routeVisit,
		routeExam,
		routeTherapy,
		routeRegister,
		routeLogin
	}
}
